<template>
    <div class="mt-2 space-x-2 flex items-center">
        <Switch
            :disabled="isCurrentlySelectedIntegrationCalendar"
            :checked="checked"
            @update:checked="
                (checked) => onSwitchUpdate(checked, integrationId, calendar)
            "
        />
        <Label :for="calendar.id">
            {{ calendar.name }}
        </Label>
    </div>
</template>

<script lang="ts" setup>
import { Calendar } from "../../../types/IntegrationCalendar";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { EventTypeUser } from "../../../types/EventTypeUser";
import { computed } from "vue";
import { router } from "@inertiajs/vue3";
import { toast } from "@/components/ui/toast";
import route from "ziggy-js";

interface Props {
    calendar: Calendar;
    integrationId: number;
    eventTypeUser: EventTypeUser;
    eventTypeId: number;
}
const props = defineProps<Props>();

const isCurrentlySelectedIntegrationCalendar = computed(() => {
    // @TODO: legacy typecheck when integration_data used to be unparsed string
    if (
        typeof props.eventTypeUser.integration_data === "string" ||
        !props.eventTypeUser.integration_data?.calendars
    ) {
        return false;
    }

    let calendarId = "";
    // check if is icloud calendar and get calendar id from caldav url
    if (
        props.eventTypeUser.integration_data?.calendars[0]?.calendar_url?.includes(
            "https://caldav.icloud.com",
        )
    ) {
        calendarId =
            props.eventTypeUser.integration_data.calendars[0].calendar_url.replace(
                "https://caldav.icloud.com",
                "",
            );
    } else {
        calendarId = props.eventTypeUser.integration_data?.calendars[0]?.id;
    }

    const integrationId = props.eventTypeUser.integration_id;

    if (
        calendarId === props.calendar.id &&
        integrationId === props.integrationId
    ) {
        return true;
    }

    return false;
});

const checked = computed(() => {
    if (isCurrentlySelectedIntegrationCalendar.value) {
        return true;
    }

    if (props.eventTypeUser.conflict_check_calendars === null) {
        return false;
    }

    if (
        typeof props.eventTypeUser.conflict_check_calendars === "object" &&
        !Array.isArray(props.eventTypeUser.conflict_check_calendars)
    ) {
        const converted = Object.values(
            props.eventTypeUser.conflict_check_calendars,
        );

        return (
            converted.findIndex(
                (calendar) =>
                    calendar.integration_id === props.integrationId &&
                    calendar.calendar_id === props.calendar.id,
            ) !== -1
        );
    } else {
        return (
            props.eventTypeUser.conflict_check_calendars.findIndex(
                (calendar) =>
                    calendar.integration_id === props.integrationId &&
                    calendar.calendar_id === props.calendar.id,
            ) !== -1
        );
    }
});

function onSwitchUpdate(
    checked: boolean,
    integrationId: number,
    calendar: Calendar,
) {
    if (checked) {
        assignConflictCheck(integrationId, calendar);
    } else {
        unassignConflictCheck(integrationId, calendar.id);
    }
}

function assignConflictCheck(
    calendarIntegrationId: number,
    calendar: Calendar,
) {
    const data = {
        integration_id: calendarIntegrationId,
        calendar_id: calendar.id,
        calendar_name: calendar.name,
        calendar_default: calendar.default,
    };

    router.post(
        route("app.event-types.conflict-check-calendars.assign", {
            eventType: props.eventTypeId,
        }),
        data,
        {
            onError: (err) => {
                toast({
                    title: "Uh oh! Something went wrong. Please contact support.",
                    description:
                        "There was a problem with your request." +
                        JSON.stringify(err),
                    variant: "destructive",
                });
            },
        },
    );
}

function unassignConflictCheck(integrationId: number, calendarId: string) {
    const data = {
        integration_id: integrationId,
        calendar_id: calendarId,
    };

    router.post(
        route("app.event-types.conflict-check-calendars.unassign", {
            eventType: props.eventTypeId,
        }),
        data,
        {
            onError: (err) => {
                toast({
                    title: "Uh oh! Something went wrong. Please contact support.",
                    description:
                        "There was a problem with your request." +
                        JSON.stringify(err),
                    variant: "destructive",
                });
            },
        },
    );
}
</script>
